// Checkout (tienda v5) — checkout REAL one-shot (Sub-fase 4.5, decisión Nico: Opción B).
//
// NO reimplementa nada: envuelve el <CheckoutFlow/> de PRODUCCIÓN (src/components/CheckoutFlow.jsx,
// wizard de 4 pasos resumen→dirección→día→pago) alimentado por el CARRITO REAL (CartContext), con el
// restyle v5 (CHECKOUT_CSS, override scopeado a .tv5-page-checkout sobre las clases .cf-*).
//
// Flujo de pago (sin código antifraude nuevo):
//   • Visitante: CheckoutFlow crea cuenta (password+Turnstile, sin email round-trip) + pedido vía la
//     edge `crear-pedido-publico` (preference MP antifraude-enriquecida) → redirect a Checkout Pro.
//   • Logueado: `cliente_crear_pedido` → /cliente/pagar-pedido/:id.
//   El webhook mp-webhook cierra el pedido→pagado y los triggers en DB disparan entrega + CFE.
//
// El Payment Brick on-page para one-shot es un refactor DEDICADO aparte (task #7) — acá NO se toca MP.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckoutFlow from '../../components/CheckoutFlow';
import { TopNav, BottomNav } from '../../components/tienda-v5';
import { useCart } from '../../lib/CartContext';
import { trackEvent } from '../../lib/metaPixel';
import { CHECKOUT_CSS } from './checkoutV5Styles';

// Arma los campos del Pixel (content_ids + contents con precio/cantidad) desde los ítems del carrito.
function pixelDesdeItems(items) {
  return {
    content_ids: items.map((it) => it.sku),
    contents: items.map((it) => ({ id: it.sku, quantity: it.cantidad, item_price: Number(it.precio_unit) || 0 })),
    content_type: 'product',
  };
}

// TEMP · instrumentación diagnóstico bug P0 "checkout logueado rebota a /catalogo" (Nico). QUITAR luego.
const DEBUG_CHECKOUT = true;
const dbg = (...a) => { if (DEBUG_CHECKOUT) { try { console.log('%c[CHK:page]', 'color:#C5A059;font-weight:700', ...a); } catch { /* noop */ } } };

export default function Checkout() {
  const navigate = useNavigate();
  const { items, itemsCount, subtotal, clear } = useCart();
  const initiateTrackeadoRef = useRef(false);

  // BUG P0 (logueado): al crear el pedido, onPedidoCreado vacía el carrito (evita re-checkout) → esto
  // dispara el guard de "carrito vacío → /catalogo" de más abajo, que compite con y PISA el
  // navigate('/cliente/pagar-pedido/:id') de CheckoutFlow → el usuario cae en /catalogo en vez de MP.
  // (El visitante no sufría esto: sale con window.location.href, que gana siempre.) Fix: marcar que ya
  // estamos pagando ANTES de vaciar (ref síncrona) para que el guard NO redirija tras el clear.
  const pagandoRef = useRef(false);

  // Cross-domain (limpack.uy → /checkout?merge_cart=...): el CartContext hidrata el merge en su propio
  // useEffect (efecto del PROVIDER = padre → corre DESPUÉS de los efectos de este hijo). Si snapshoteamos
  // el carrito o decidimos el redirect por vacío en el mount, lo hacemos ANTES de que el merge llegue →
  // checkout vacío / bounce a /catalogo. Fix: si venimos con merge_cart, esperamos un tick (`ready`) a
  // que hidrate antes de snapshotear itemsIniciales y de evaluar el carrito vacío.
  const [mergePendiente] = useState(() => {
    try { return new URLSearchParams(window.location.search).has('merge_cart'); } catch { return false; }
  });
  const [ready, setReady] = useState(!mergePendiente);
  useEffect(() => {
    if (ready) return;
    const t = setTimeout(() => setReady(true), 80); // suficiente para el efecto de merge del CartContext
    return () => clearTimeout(t);
  }, [ready]);

  // Snapshot del carrito cuando ya está listo (post-hidratación si hubo merge). CheckoutFlow copia
  // itemsIniciales a su propio estado. Forma del ítem = la que espera CheckoutFlow.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const itemsIniciales = useMemo(() => items.map(it => ({
    sku: it.sku, nombre: it.nombre, presentacion: it.presentacion,
    precio_unit: it.precio_unit, cantidad: it.cantidad,
  })), [ready]);

  // Carrito vacío (ya hidratado) → no hay nada que pagar; volver al catálogo. PERO si ya iniciamos el
  // pago (pagandoRef), el vaciado es intencional (post-creación del pedido) → NO redirigir a /catalogo,
  // dejamos que CheckoutFlow lleve a la pantalla de pago MP.
  useEffect(() => {
    dbg('guard carrito-vacío · eval', { ready, itemsCount, pagando: pagandoRef.current });
    if (ready && itemsCount === 0 && !pagandoRef.current) {
      dbg('guard carrito-vacío · DISPARA navigate(/catalogo)', { ready, itemsCount, pagando: pagandoRef.current });
      navigate('/catalogo', { replace: true });
    }
  }, [ready, itemsCount, navigate]);

  // Meta Pixel: InitiateCheckout una sola vez, cuando el carrito ya está hidratado y tiene ítems.
  useEffect(() => {
    if (!ready || itemsCount === 0 || initiateTrackeadoRef.current) return;
    initiateTrackeadoRef.current = true;
    trackEvent('InitiateCheckout', {
      ...pixelDesdeItems(items),
      value: subtotal,
      currency: 'UYU',
      num_items: itemsCount,
    });
  }, [ready, itemsCount, items, subtotal]);

  if (!ready) return null;          // esperando hidratación del merge cross-domain
  if (itemsCount === 0 && !pagandoRef.current) { dbg('render → null (carrito vacío, no pagando)'); return null; } // redirigiendo

  return (
    <div className="tv5-page-checkout">
      <style>{CHECKOUT_CSS}</style>
      {/* Nav v5 público en el checkout (visitantes Y clientes usan el nav público acá; el shell del
          cliente aplica DESPUÉS del pago en /cliente/pagar-pedido). El BottomNav fixed en mobile: dejamos
          padding-bottom para que no tape el CTA del wizard. */}
      <style>{`@media(max-width:899px){.tv5-page-checkout{padding-bottom:calc(76px + env(safe-area-inset-bottom,0px))}}`}</style>
      <TopNav accent="teal" />
      <CheckoutFlow
        tipo="productos_sueltos"
        itemsIniciales={itemsIniciales}
        tituloHero="Tu pedido"
        onCancelar={() => { dbg('onCancelar → navigate(/catalogo)'); navigate('/catalogo'); }}
        onPedidoCreado={() => {
          dbg('onPedidoCreado → pagandoRef=true + Purchase + clear()');
          pagandoRef.current = true;
          // Meta Pixel: Purchase al confirmar el pedido (ANTES de vaciar el carrito). El pago real
          // lo cierra el webhook server-side; esto es la señal client-side para los anuncios de catálogo.
          try { trackEvent('Purchase', { ...pixelDesdeItems(items), value: subtotal, currency: 'UYU', num_items: itemsCount }); } catch { /* noop */ }
          try { clear(); } catch { /* noop */ }
        }}
        onCompletado={() => { dbg('onCompletado → navigate(/gracias)'); navigate('/gracias', { replace: true }); }}
      />
      <BottomNav active="carrito" />
    </div>
  );
}
